import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";
import { Container } from "./Container";
import { Logo } from "./Logo";
import { StripeMark } from "./StripeMark";
import { scrollToId } from "./AppButtons";
import { openCookiePreferences } from "../lib/consent";

export function Footer() {
  const { t, locale } = useI18n();
  const L = (p) => `/${locale}${p}`;

  const cols = [
    {
      title: t("footer.colProduct"),
      links: [
        { label: t("footer.links.home"), to: L("") },
        { label: t("footer.links.features"), to: L("#features") },
        { label: t("pricing.label"), to: L("#pricing") },
        { label: t("nav.how"), to: L("#how") },
        { label: t("footer.links.faq"), to: L("#faq") },
        { label: t("footer.links.download"), to: L("#download") },
      ],
    },
    {
      title: t("footer.colCompany"),
      links: [
        { label: t("footer.links.contact"), to: L("/contact") },
        { label: t("footer.links.support"), to: L("/support") },
      ],
    },
    {
      title: t("footer.colLegal"),
      links: [
        { label: t("footer.links.privacy"), to: L("/privacy") },
        { label: t("footer.links.terms"), to: L("/terms") },
        { label: t("legalx.navCookies"), to: L("/cookies") },
      ],
    },
  ];

  return (
    <footer className="relative overflow-hidden bg-[#0A0A0A] text-white">
      <div className="noise pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-green/10 blur-3xl" />

      <Container className="relative py-20 md:py-28">
        {/* Brand statement + CTA */}
        <div className="flex flex-col gap-10 border-b border-white/10 pb-16 md:flex-row md:items-end md:justify-between">
          <h2 className="max-w-2xl text-4xl font-semibold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
            {t("footer.statement1")}{" "}
            <span className="brand-text">{t("footer.statement2")}</span>
          </h2>
          <div className="flex-shrink-0">
            <button
              type="button"
              data-testid="footer-download-cta"
              onClick={() => scrollToId("download")}
              className="focus-ring group inline-flex items-center gap-2 rounded-full brand-gradient px-7 py-4 text-base font-medium text-white transition-[opacity,transform] duration-200 hover:opacity-95 active:scale-[0.98]"
            >
              {t("footer.download")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
            </button>
            <p className="mt-3 text-sm text-white/40">{t("hero.availability")}</p>
          </div>
        </div>

        {/* Links + brand */}
        <div className="grid grid-cols-2 gap-10 pt-16 md:grid-cols-5 md:gap-8">
          <div className="col-span-2">
            <Logo height={40} dark />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-white/50">{t("footer.tagline")}</p>
            <a
              href="mailto:support@centauros.app"
              data-testid="footer-contact-email"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-white underline decoration-green decoration-2 underline-offset-4 transition-colors hover:text-green"
            >
              support@centauros.app
            </a>
          </div>

          {cols.map((col) => (
            <div key={col.title}>
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">{col.title}</h3>
              <ul className="mt-5 space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-white/60 transition-colors duration-200 hover:text-white"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center">
          <p className="text-sm text-white/40">{t("footer.rights")}</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/40">
            <button
              type="button"
              onClick={openCookiePreferences}
              data-testid="footer-cookie-preferences"
              className="transition-colors duration-200 hover:text-white"
            >
              {t("cookie.reopen")}
            </button>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-green" />
              <StripeMark className="opacity-90 brightness-0 invert" /> · {t("footer.secure")}
            </span>
          </div>
        </div>
      </Container>
    </footer>
  );
}
